import { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Scatter, Line, ComposedChart, Cell, PieChart, Pie,
} from "recharts";

import { InfoIcon, WithTooltip, getMetricInfo, getChartInfo } from "./metricInfo";
import { getEvalWerCsvUrl } from "../../services/api";

const PERCENT_SUFFIXES = ["_pct", "_rate", "_frac"];
const PERCENT_PREFIXES = ["wer_", "corpus_wer_", "corpus_llm_wer_"];
const NON_PERCENT_IDS = new Set(["wer_n_rows", "n_rows"]);

export function isPercentMetric(id) {
  if (!id) return false;
  if (NON_PERCENT_IDS.has(id)) return false;
  if (id.startsWith("regressions_")) return false;
  if (PERCENT_SUFFIXES.some((s) => id.endsWith(s))) return true;
  if (PERCENT_PREFIXES.some((p) => id.startsWith(p))) return true;
  return false;
}

export function formatMetricValue(metric) {
  const { id, value, unit } = metric || {};
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (isPercentMetric(id)) {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (typeof value === "number" && !Number.isInteger(value)) {
    const abs = Math.abs(value);
    const digits = abs >= 100 ? 1 : abs >= 1 ? 2 : 3;
    return unit ? `${value.toFixed(digits)} ${unit}` : value.toFixed(digits);
  }
  return unit ? `${value} ${unit}` : String(value);
}

export function MetricTile({ metric }) {
  const info = getMetricInfo(metric);
  return (
    <div className="eval-metric-tile">
      <div className="eval-metric-title">
        <span>{metric.title}</span>
        <InfoIcon text={info} />
      </div>
      <div className="eval-metric-value">{formatMetricValue(metric)}</div>
    </div>
  );
}

function ChartCard({ title, info, caption, rawData, children, wide = false }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className={`eval-chart-card ${wide ? "wide" : ""}`}>
      <div className="eval-chart-header">
        <h3>
          <span>{title}</span>
          <InfoIcon text={info} />
        </h3>
        <button
          type="button"
          className="eval-raw-toggle"
          onClick={() => setShowRaw((v) => !v)}
        >
          {showRaw ? "Hide raw" : "View raw data"}
        </button>
      </div>
      <div className="eval-chart-body">{children}</div>
      {caption && <div className="eval-chart-caption">{caption}</div>}
      {showRaw && (
        <pre className="eval-chart-raw">{JSON.stringify(rawData, null, 2)}</pre>
      )}
    </div>
  );
}

function isEmptyChart(chart) {
  if (!chart) return true;
  if (chart.type === "histogram") {
    return !Array.isArray(chart.counts) || chart.counts.length === 0;
  }
  if (chart.type === "bar") {
    return !Array.isArray(chart.x) || chart.x.length === 0;
  }
  if (chart.type === "scatter") {
    return !Array.isArray(chart.x) || chart.x.length === 0;
  }
  if (chart.type === "table") {
    return !Array.isArray(chart.rows) || chart.rows.length === 0;
  }
  return false;
}

const DRILLDOWN_COLUMN_KEYS = new Set(["annotation_id", "audio", "audio_path"]);

function isDrillDownKey(key) {
  if (key == null) return false;
  const norm = String(key).toLowerCase().trim();
  return DRILLDOWN_COLUMN_KEYS.has(norm);
}

function normalizeColumns(chart) {
  const rows = chart.rows || [];
  if (rows.length === 0) return { columns: [], getCell: () => null };

  if (Array.isArray(rows[0])) {
    const raw = chart.columns || rows[0].map((_, i) => `col_${i}`);
    const columns = raw.map((c, i) => {
      if (typeof c === "string") return { key: i, title: c, drill: isDrillDownKey(c) };
      return { key: c.key ?? i, title: c.title ?? String(c.key ?? i), drill: c.drill ?? isDrillDownKey(c.key ?? c.title) };
    });
    const getCell = (row, col) => row[col.key];
    return { columns, getCell };
  }

  const keys = chart.columns
    ? chart.columns.map((c) => (typeof c === "string" ? c : c.key))
    : Object.keys(rows[0]);
  const titles = chart.columns
    ? chart.columns.map((c) => (typeof c === "string" ? c : c.title ?? c.key))
    : keys;
  const columns = keys.map((k, i) => ({ key: k, title: titles[i], drill: isDrillDownKey(k) }));
  const getCell = (row, col) => row[col.key];
  return { columns, getCell };
}

function copyToClipboard(text) {
  if (!text) return Promise.resolve(false);
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(String(text)).then(() => true).catch(() => false);
  }
  return Promise.resolve(false);
}

function formatTableCell(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "—";
    if (!Number.isInteger(value)) return value.toFixed(3);
    return value.toLocaleString();
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function DrillDownCell({ value, onDrill, runId }) {
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const text = formatTableCell(value);
  const disabled = !runId || busy;

  const handle = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      await onDrill(value);
      setFlash(true);
      setTimeout(() => setFlash(false), 1400);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`eval-drill-link ${flash ? "flash" : ""}`}
      onClick={handle}
      disabled={disabled}
      title={runId ? "Copy ID + download per-row CSV" : "No run id available"}
    >
      {text}
      {flash && <span className="eval-drill-flash">copied</span>}
    </button>
  );
}

function TableChart({ chart, runId }) {
  const { columns, getCell } = useMemo(() => normalizeColumns(chart), [chart]);
  const rows = chart.rows || [];
  const colDescs = chart.column_descriptions || chart.col_descriptions || [];

  const handleDrill = async (annotationId) => {
    if (!runId || annotationId == null) return;
    await copyToClipboard(annotationId);
    try {
      const { url } = await getEvalWerCsvUrl(runId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || String(err);
      // eslint-disable-next-line no-alert
      alert(`Could not open per-row CSV: ${detail}`);
    }
  };

  return (
    <div className="eval-table-wrapper">
      <table className="eval-data-table">
        <thead>
          <tr>
            {columns.map((col, i) => {
              const desc = typeof colDescs === "object" && !Array.isArray(colDescs)
                ? colDescs[col.key]
                : colDescs[i];
              return (
                <th key={String(col.key)}>
                  <WithTooltip text={desc}>
                    {col.title}
                  </WithTooltip>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {columns.map((col) => {
                const v = getCell(row, col);
                if (col.drill) {
                  return (
                    <td key={String(col.key)} className="eval-table-cell drill">
                      <DrillDownCell value={v} onDrill={handleDrill} runId={runId} />
                    </td>
                  );
                }
                return (
                  <td key={String(col.key)} className="eval-table-cell">
                    {formatTableCell(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!runId && columns.some((c) => c.drill) && (
        <div className="eval-chart-caption">
          Drill-down disabled — no run id resolved for this payload.
        </div>
      )}
    </div>
  );
}

function HistogramChart({ chart }) {
  const data = (chart.counts || []).map((count, i) => {
    const lo = chart.edges?.[i];
    const hi = chart.edges?.[i + 1];
    const mid = lo !== undefined && hi !== undefined ? (lo + hi) / 2 : i;
    const label =
      lo !== undefined && hi !== undefined
        ? `[${formatBinEdge(lo)}, ${formatBinEdge(hi)})`
        : String(i);
    return { label, mid, count };
  });
  const isHindi = looksHindi(chart.title) || looksHindi(chart.x_label);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 16, bottom: 28, left: 8 }}>
        <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fontFamily: isHindi ? '"Noto Sans Devanagari", sans-serif' : undefined }}
          angle={-30}
          textAnchor="end"
          height={50}
          interval="preserveStartEnd"
        >
          <tspan>{chart.x_label}</tspan>
        </XAxis>
        <YAxis tick={{ fontSize: 11 }} label={{ value: chart.y_label, angle: -90, position: "insideLeft", fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="count" fill="#f25f3a" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function formatBinEdge(value) {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2);
}

function looksHindi(s) {
  if (!s) return false;
  return /[ऀ-ॿ]/.test(s);
}

function chartHasDevanagariLabels(chart) {
  if (!Array.isArray(chart.x)) return false;
  return chart.x.some((v) => typeof v === "string" && looksHindi(v));
}

const WER_BUCKET_COLORS = ["#2e7d32", "#9ccc65", "#fdd835", "#fb8c00", "#e53935"];

function BarChartGeneric({ chart }) {
  const horizontal = chart.id === "top_tokens" || chart.id === "rarest_words" || chartHasDevanagariLabels(chart);
  const hasMeanMedian = Array.isArray(chart.y_mean) && Array.isArray(chart.y_median);
  const asPercent = chart.id === "wer_by_age_tier";
  const isHindi = chartHasDevanagariLabels(chart) || looksHindi(chart.title);

  const data = (chart.x || []).map((x, i) => {
    const row = { x };
    if (hasMeanMedian) {
      row.mean = chart.y_mean[i];
      row.median = chart.y_median[i];
    } else {
      row.y = chart.y?.[i];
    }
    if (Array.isArray(chart.n)) row.n = chart.n[i];
    if (Array.isArray(chart.share)) row.share = chart.share[i];
    return row;
  });

  const yFormatter = asPercent ? (v) => `${(v * 100).toFixed(1)}%` : undefined;
  const tickFontFamily = isHindi ? '"Noto Sans Devanagari", sans-serif' : undefined;

  const tooltipFormatter = (value, name, ctx) => {
    const payload = ctx?.payload || {};
    if (asPercent && typeof value === "number") {
      return [`${(value * 100).toFixed(2)}%${payload.n != null ? ` (n=${payload.n})` : ""}`, name];
    }
    if (payload.n != null) return [`${value} (n=${payload.n})`, name];
    if (payload.share != null) return [`${value} (${(payload.share * 100).toFixed(1)}%)`, name];
    return [value, name];
  };

  if (horizontal) {
    const height = Math.max(280, data.length * 18);
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 80 }}>
          <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis
            type="category"
            dataKey="x"
            tick={{ fontSize: 11, fontFamily: tickFontFamily }}
            width={140}
            interval={0}
          />
          <Tooltip formatter={tooltipFormatter} />
          <Bar dataKey="y" fill="#f25f3a" />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 10, right: 16, bottom: 28, left: 8 }}>
        <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
        <XAxis
          dataKey="x"
          tick={{ fontSize: 11, fontFamily: tickFontFamily }}
          angle={-25}
          textAnchor="end"
          height={56}
          interval={0}
        />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={yFormatter} />
        <Tooltip formatter={tooltipFormatter} />
        {hasMeanMedian && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {hasMeanMedian ? (
          <>
            <Bar dataKey="mean" name="mean" fill="#f25f3a" />
            <Bar dataKey="median" name="median" fill="#3a8df2" />
          </>
        ) : chart.id === "wer_buckets" ? (
          <Bar dataKey="y">
            {data.map((_, i) => (
              <Cell key={i} fill={WER_BUCKET_COLORS[i % WER_BUCKET_COLORS.length]} />
            ))}
          </Bar>
        ) : (
          <Bar dataKey="y" fill="#f25f3a" />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

// Shared helper for grouped_bar / stacked_bar: pivot `{x: [], series: {name: [values]}}`
// into the row-per-x shape recharts expects.
function buildSeriesData(chart) {
  const xs = chart.x || [];
  const seriesKeys = Object.keys(chart.series || {});
  const data = xs.map((x, i) => {
    const row = { x };
    seriesKeys.forEach((s) => {
      const v = chart.series[s];
      if (Array.isArray(v)) row[s] = v[i];
      else if (v && typeof v === "object") row[s] = v[x];
    });
    return row;
  });
  return { data, seriesKeys };
}

const SERIES_COLORS = ["#f25f3a", "#3a8df2", "#9ccc65", "#fdd835", "#ab47bc", "#26a69a"];

function GroupedBarChart({ chart, stacked = false }) {
  const { data, seriesKeys } = buildSeriesData(chart);
  const percentY = chart.percent_y !== false; // default true for WER-like charts
  const yFormatter = percentY ? (v) => `${(v * 100).toFixed(0)}%` : undefined;
  const tooltipFormatter = (value, name) => {
    if (value === null || value === undefined) return ["—", name];
    if (percentY && typeof value === "number") return [`${(value * 100).toFixed(2)}%`, name];
    return [value, name];
  };
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 10, right: 16, bottom: 56, left: 8 }}>
        <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
        <XAxis dataKey="x" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={56} interval={0} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={yFormatter} />
        <Tooltip formatter={tooltipFormatter} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {seriesKeys.map((s, i) => (
          <Bar
            key={s}
            dataKey={s}
            fill={SERIES_COLORS[i % SERIES_COLORS.length]}
            name={s}
            stackId={stacked ? "a" : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function HeatmapChart({ chart }) {
  // Accept either {rows, cols, values:[[]] } or {y, x, values:[[]] }.
  const rows = chart.rows || chart.y || [];
  const cols = chart.cols || chart.x || [];
  // `values` may be a flat 2D array OR an object keyed by row → array of col values.
  const rawValues = chart.values || chart.matrix || chart.data || [];
  const getCell = (r, c) => {
    if (Array.isArray(rawValues)) return rawValues[r]?.[c];
    if (rawValues && typeof rawValues === "object") {
      const rowVal = rawValues[rows[r]];
      if (Array.isArray(rowVal)) return rowVal[c];
      if (rowVal && typeof rowVal === "object") return rowVal[cols[c]];
    }
    return undefined;
  };
  // Compute min/max for color scaling.
  const flat = [];
  for (let r = 0; r < rows.length; r += 1) {
    for (let c = 0; c < cols.length; c += 1) {
      const v = getCell(r, c);
      if (typeof v === "number" && Number.isFinite(v)) flat.push(v);
    }
  }
  const min = flat.length ? Math.min(...flat) : 0;
  const max = flat.length ? Math.max(...flat) : 1;
  const colorFor = (v) => {
    if (v === null || v === undefined || Number.isNaN(v)) return "rgba(127,127,127,0.1)";
    const t = max === min ? 0.5 : (v - min) / (max - min);
    // Green (low/good) → red (high/bad). Flip if you want the opposite.
    const hue = (1 - t) * 120;
    return `hsl(${hue}, 65%, 50%)`;
  };
  const isPercent = chart.percent_values !== false;
  const fmt = (v) => {
    if (typeof v !== "number" || !Number.isFinite(v)) return "—";
    return isPercent ? `${(v * 100).toFixed(1)}%` : v.toFixed(3);
  };
  return (
    <div className="eval-heatmap-wrapper" style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            <th />
            {cols.map((c) => (
              <th key={c} style={{ padding: "4px 8px", fontWeight: 500, textAlign: "center" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={r}>
              <th style={{ padding: "4px 8px", fontWeight: 500, textAlign: "right" }}>{r}</th>
              {cols.map((c, ci) => {
                const v = getCell(ri, ci);
                return (
                  <td
                    key={c}
                    style={{
                      padding: "6px 10px",
                      textAlign: "center",
                      background: colorFor(v),
                      color: "#fff",
                      minWidth: 60,
                    }}
                    title={`${r} · ${c} = ${fmt(v)}`}
                  >
                    {fmt(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScatterPlot({ chart }) {
  const data = (chart.x || []).map((x, i) => ({ x, y: chart.y?.[i] }));
  let fitData = null;
  if (chart.fit && data.length > 1) {
    const xs = data.map((d) => d.x);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const { slope, intercept } = chart.fit;
    fitData = [
      { x: minX, y: slope * minX + intercept },
      { x: maxX, y: slope * maxX + intercept },
    ];
  }
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 10, right: 16, bottom: 28, left: 8 }}>
        <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
        <XAxis type="number" dataKey="x" tick={{ fontSize: 11 }} label={{ value: chart.x_label, position: "insideBottom", offset: -6, fontSize: 11 }} />
        <YAxis type="number" dataKey="y" tick={{ fontSize: 11 }} label={{ value: chart.y_label, angle: -90, position: "insideLeft", fontSize: 11 }} />
        <Tooltip />
        <Scatter data={data} fill="#f25f3a" />
        {fitData && (
          <Line
            type="linear"
            data={fitData}
            dataKey="y"
            stroke="#3a8df2"
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function ErrorTypeBreakdown({ chart }) {
  const data = (chart.x || []).map((x, i) => ({
    name: x,
    value: chart.y?.[i] ?? 0,
    share: chart.share?.[i],
  }));
  const colors = ["#f25f3a", "#3a8df2", "#9ccc65"];
  return (
    <div className="eval-error-breakdown">
      <div style={{ flex: 1, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 10, right: 16, bottom: 28, left: 8 }}>
            <CartesianGrid stroke="rgba(127,127,127,0.15)" strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(v, _n, ctx) => {
                const share = ctx?.payload?.share;
                return share != null ? [`${v} (${(share * 100).toFixed(1)}%)`, "count"] : [v, "count"];
              }}
            />
            <Bar dataKey="value">
              {data.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {data.some((d) => d.share != null) && (
        <div style={{ width: 200 }}>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={data.map((d) => ({ name: d.name, value: d.share ?? 0 }))}
                dataKey="value"
                nameKey="name"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={2}
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function ChartRenderer({ chart, runId }) {
  if (isEmptyChart(chart)) return null;

  const caption = chart.id === "zipf_loglog" && chart.fit
    ? `Zipf slope: ${chart.fit.slope.toFixed(3)}`
    : undefined;

  let body;
  if (chart.type === "histogram") {
    body = <HistogramChart chart={chart} />;
  } else if (chart.type === "scatter") {
    body = <ScatterPlot chart={chart} />;
  } else if (chart.type === "bar" && chart.id === "error_type_breakdown") {
    body = <ErrorTypeBreakdown chart={chart} />;
  } else if (chart.type === "bar") {
    body = <BarChartGeneric chart={chart} />;
  } else if (chart.type === "grouped_bar") {
    body = <GroupedBarChart chart={chart} stacked={false} />;
  } else if (chart.type === "stacked_bar") {
    body = <GroupedBarChart chart={chart} stacked={true} />;
  } else if (chart.type === "heatmap") {
    body = <HeatmapChart chart={chart} />;
  } else if (chart.type === "table") {
    body = <TableChart chart={chart} runId={runId} />;
  } else {
    body = <div className="eval-unsupported">Unsupported chart type: {chart.type}</div>;
  }

  const wide = chart.id === "top_tokens" || chart.id === "rarest_words" || chart.id === "error_type_breakdown" || chart.type === "table";

  return (
    <ChartCard title={chart.title} info={getChartInfo(chart)} caption={caption} rawData={chart} wide={wide}>
      {body}
    </ChartCard>
  );
}